import { UnitConverter } from '../base/UnitConverter';
import { UnitOptions } from '../constants/unit.options';
import { CountPolicy } from '../policy';
import { QuantityResult } from '../types';

export class TemperatureUnitConverter extends UnitConverter<
  (typeof UnitOptions)['Temperature'][number]['value']
> {
  // 싱글턴 인스턴스
  private static instance: TemperatureUnitConverter;

  private constructor() {
    super();
  }

  static getInstance(): TemperatureUnitConverter {
    if (!TemperatureUnitConverter.instance) {
      TemperatureUnitConverter.instance = new TemperatureUnitConverter();
    }
    return TemperatureUnitConverter.instance;
  }

  protected units = [
    'decidegree celsius (d°C)',
    'degree celsius (°C)',
    'degree fahrenheit (°F)',
  ] as const;

  // d°C는 0.1 °C
  protected unitToBase = {
    'decidegree celsius (d°C)': 0.1,
    'degree celsius (°C)': 1,
    // °F는 offset이 있어 unitToBase를 사용하지 않습니다(참고용 숫자 넣지 않음).
    'degree fahrenheit (°F)': 1, // (사용 안 함)
  } as const;

  // °F ↔ °C, d°C ↔ °C 변환을 직접 처리
  override convertToUnit(
    value: number,
    fromUnit: string,
    toUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    if (!this.isValidUnit(fromUnit) || !this.isValidUnit(toUnit)) {
      // 알 수 없는 단위는 원래 로직대로 패스
      return policy(value, toUnit);
    }

    // 1) from → °C
    let c = value;
    if (fromUnit === 'decidegree celsius (d°C)') {
      c = value * 0.1;
    } else if (fromUnit === 'degree fahrenheit (°F)') {
      c = (value - 32) / 1.8;
    }

    // 2) °C → to
    let out = c;
    if (toUnit === 'decidegree celsius (d°C)') {
      out = c * 10;
    } else if (toUnit === 'degree fahrenheit (°F)') {
      out = c * 1.8 + 32;
    }

    return policy(out, toUnit);
  }

  // 온도는 "베스트 단위" 자동 스케일링을 하지 않는 편이 자연스럽습니다(입력 단위를 유지).
  override convertToBestUnit(
    value: number,
    fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    return policy(value, fromUnit);
  }

  override convertToLargerBestUnit(
    value: number,
    fromUnit: string,
    countPolicy?: CountPolicy,
  ): QuantityResult {
    const policy = this.resolveCountPolicy(countPolicy);

    return policy(value, fromUnit);
  }
}
